import React from 'react';
import {
  Building2, TrendingUp, ShieldCheck, Coins, Globe2, Users2,
  Search, Hammer, ArrowUpRight, Banknote, CreditCard, ChevronRight
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const T = {
  de: {
    eyebrow: 'Warum Löwenherz Invest AG',
    title: 'Schweizer Substanz.',
    titleHighlight: 'Attraktive Zielrendite.',
    intro: 'Wir kaufen gezielt unterbewertete Schweizer Immobilien, optimieren sie durch professionelles Asset-Management und verkaufen sie gewinnbringend weiter. Die daraus entstehenden Mittel fliessen als attraktive Brutto-Dividende direkt an unsere Aktionäre zurück.',
    benefits: [
      { icon: Coins, title: 'Zieldividende 6–9% p.a.',
        body: 'Angestrebte Brutto-Dividende auf Basis optimierter Mieteinnahmen und gewinnbringender Immobilien-Verkäufe. Keine Garantie – abhängig vom Geschäftsverlauf und dem Beschluss der Generalversammlung.' },
      { icon: ShieldCheck, title: 'Sicherung in CHF',
        body: 'Raus aus Euro und Dollar – Ihr Vermögen ist in Schweizer Franken und realen Sachwerten gebunden.' },
      { icon: Building2, title: 'Direktes Eigentum',
        body: 'Sie sind Miteigentümer real existierender Schweizer Immobilien – kein Fonds, kein Zertifikat, kein Derivat.' },
      { icon: Users2, title: 'Volles Mitspracherecht',
        body: 'Jede Aktie = eine Stimme an der Generalversammlung. Sie entscheiden mit über Strategie und Ausschüttungen.' },
      { icon: Globe2, title: 'Internationale Auszahlung',
        body: 'Brutto-Dividende auf jedes gewünschte Konto weltweit. Bald auch mit eigener Löwenherz-Kreditkarte verfügbar.' },
      { icon: TrendingUp, title: 'Optimierte Vererbung',
        body: 'Aktien lassen sich unkompliziert und steuerlich optimal an die nächste Generation übertragen.' },
    ],
    processTitle: 'So entsteht Ihre Rendite',
    processSubtitle: 'Unsere bewährte vier-Stufen-Wertschöpfung',
    steps: [
      { icon: Search,        title: 'Aufspüren',     body: 'Wir identifizieren unterbewertete Schweizer Immobilien mit klarem Potenzial – Lage, Substanz, Mietsituation.' },
      { icon: Hammer,        title: 'Optimieren',    body: 'Renovierung, Modernisierung und professionelle Vermietung steigern Wert und Mieteinnahmen nachhaltig.' },
      { icon: ArrowUpRight,  title: 'Veräussern',    body: 'Zum optimalen Zeitpunkt verkaufen wir Objekte mit attraktivem Gewinn an institutionelle Käufer.' },
      { icon: Banknote,      title: 'Ausschütten',   body: 'Die Gewinne fliessen als Brutto-Dividende direkt auf Ihr Konto – wohin auch immer Sie es wünschen.' },
    ],
    minTitle: 'Einstieg bereits ab',
    minValue: 'CHF 3 000',
    minBody: 'Werden Sie mit einer überschaubaren Mindesteinlage offiziell eingetragener Aktionär unserer Gesellschaft – Eintragung direkt im offiziellen Schweizer Aktienregister.',
    cardTitle: 'Bald: Löwenherz Karte',
    cardBody: 'Ihre Dividende direkt auf Ihre persönliche Löwenherz-Kreditkarte – flexibel weltweit verfügbar, wenn Sie sie brauchen.',
    cta: 'Jetzt Aktionär werden',
  },
  en: {
    eyebrow: 'Why Löwenherz Invest AG',
    title: 'Swiss substance.',
    titleHighlight: 'Attractive target yield.',
    intro: 'We acquire undervalued Swiss real estate, optimise it through professional asset management and resell at a profit. The resulting cash flows are returned to our shareholders as an attractive gross dividend.',
    benefits: [
      { icon: Coins, title: 'Target dividend 6–9% p.a.',
        body: 'Targeted gross dividend based on optimised rental income and profitable property sales. No guarantee – depending on business performance and the decision of the general meeting.' },
      { icon: ShieldCheck, title: 'Secured in CHF',
        body: 'Out of euro and dollar – your wealth is anchored in Swiss francs and tangible real assets.' },
      { icon: Building2, title: 'Direct ownership',
        body: 'You are a real co-owner of existing Swiss properties – no fund, no certificate, no derivative.' },
      { icon: Users2, title: 'Full voting rights',
        body: 'One share = one vote at the general meeting. You help decide on strategy and dividends.' },
      { icon: Globe2, title: 'International payout',
        body: 'Gross dividend to any account worldwide. Soon also via our own Löwenherz credit card.' },
      { icon: TrendingUp, title: 'Optimised inheritance',
        body: 'Shares can be transferred simply and tax-efficiently to the next generation.' },
    ],
    processTitle: 'How your yield is created',
    processSubtitle: 'Our proven four-stage value chain',
    steps: [
      { icon: Search,       title: 'Identify',  body: 'We spot undervalued Swiss properties with clear potential – location, substance, rental situation.' },
      { icon: Hammer,       title: 'Optimise',  body: 'Renovation, modernisation and professional letting sustainably increase value and rental income.' },
      { icon: ArrowUpRight, title: 'Divest',    body: 'At the optimal moment we sell objects with an attractive profit to institutional buyers.' },
      { icon: Banknote,     title: 'Distribute',body: 'Profits flow back as gross dividends directly to your account – wherever you want it.' },
    ],
    minTitle: 'Start from',
    minValue: 'CHF 3,000',
    minBody: 'Become an officially registered shareholder with a manageable minimum investment – entry directly into the official Swiss share register.',
    cardTitle: 'Coming soon: Löwenherz Card',
    cardBody: 'Your dividend directly on your personal Löwenherz credit card – flexibly available worldwide whenever you need it.',
    cta: 'Become a shareholder',
  },
  ru: {
    eyebrow: 'Почему Löwenherz Invest AG',
    title: 'Швейцарская основательность.',
    titleHighlight: 'Привлекательная целевая доходность.',
    intro: 'Мы целенаправленно покупаем недооценённую швейцарскую недвижимость, оптимизируем её с помощью профессионального управления активами и выгодно перепродаём. Полученные средства возвращаются нашим акционерам в виде привлекательной брутто-дивиденды.',
    benefits: [
      { icon: Coins, title: 'Целевой дивиденд 6–9% годовых',
        body: 'Планируемая брутто-дивиденда за счёт оптимизированных арендных доходов и прибыльных продаж недвижимости. Без гарантии — зависит от результатов деятельности и решения общего собрания акционеров.' },
      { icon: ShieldCheck, title: 'Защита в CHF',
        body: 'Выход из евро и доллара – ваш капитал привязан к швейцарскому франку и реальным активам.' },
      { icon: Building2, title: 'Прямое владение',
        body: 'Вы — реальный совладелец швейцарской недвижимости. Не фонд, не сертификат, не дериватив.' },
      { icon: Users2, title: 'Полное право голоса',
        body: 'Одна акция = один голос на общем собрании. Вы участвуете в принятии решений о стратегии и распределении прибыли.' },
      { icon: Globe2, title: 'Международные выплаты',
        body: 'Брутто-дивиденда на любой счёт в мире. Скоро также через собственную кредитную карту Löwenherz.' },
      { icon: TrendingUp, title: 'Оптимизированное наследование',
        body: 'Акции легко и налогово выгодно передаются следующему поколению.' },
    ],
    processTitle: 'Как формируется ваша доходность',
    processSubtitle: 'Наша проверенная 4-ступенчатая цепочка создания стоимости',
    steps: [
      { icon: Search,       title: 'Поиск',         body: 'Мы выявляем недооценённую швейцарскую недвижимость с явным потенциалом — локация, состояние, аренда.' },
      { icon: Hammer,       title: 'Оптимизация',   body: 'Ремонт, модернизация и профессиональная сдача в аренду устойчиво повышают стоимость и доходы.' },
      { icon: ArrowUpRight, title: 'Продажа',       body: 'В оптимальный момент мы выгодно продаём объекты институциональным покупателям.' },
      { icon: Banknote,     title: 'Выплата',       body: 'Прибыль возвращается в виде брутто-дивиденды прямо на ваш счёт — куда бы вы ни захотели.' },
    ],
    minTitle: 'Минимальный вход',
    minValue: 'CHF 3 000',
    minBody: 'Станьте официально зарегистрированным акционером с разумной минимальной суммой — внесение прямо в официальный швейцарский реестр акций.',
    cardTitle: 'Скоро: карта Löwenherz',
    cardBody: 'Ваши дивиденды напрямую на персональную кредитную карту Löwenherz — гибко доступно по всему миру.',
    cta: 'Стать акционером',
  },
  vi: {
    eyebrow: 'Tại sao chọn Löwenherz Invest AG',
    title: 'Nền tảng Thụy Sĩ.',
    titleHighlight: 'Lợi nhuận mục tiêu hấp dẫn.',
    intro: 'Chúng tôi mua các bất động sản Thụy Sĩ bị định giá thấp, tối ưu hóa qua quản lý tài sản chuyên nghiệp và bán lại với lợi nhuận cao. Dòng tiền sinh ra được trả lại cho cổ đông dưới dạng cổ tức gộp hấp dẫn.',
    benefits: [
      { icon: Coins, title: 'Cổ tức mục tiêu 6–9%/năm',
        body: 'Cổ tức gộp hướng đến dựa trên thu nhập cho thuê tối ưu và doanh thu bán bất động sản có lãi. Không có bảo đảm – phụ thuộc vào kết quả kinh doanh và quyết định của đại hội cổ đông.' },
      { icon: ShieldCheck, title: 'Bảo toàn bằng CHF',
        body: 'Thoát khỏi Euro và Dollar – tài sản của bạn được neo bằng franc Thụy Sĩ và tài sản thực.' },
      { icon: Building2, title: 'Sở hữu trực tiếp',
        body: 'Bạn là đồng sở hữu thực sự của bất động sản Thụy Sĩ – không phải quỹ, không chứng chỉ, không phái sinh.' },
      { icon: Users2, title: 'Quyền biểu quyết đầy đủ',
        body: 'Một cổ phiếu = một phiếu bầu tại đại hội. Bạn cùng quyết định chiến lược và cổ tức.' },
      { icon: Globe2, title: 'Thanh toán quốc tế',
        body: 'Cổ tức gộp tới bất kỳ tài khoản nào trên thế giới. Sắp tới cũng có thẻ tín dụng Löwenherz riêng.' },
      { icon: TrendingUp, title: 'Thừa kế tối ưu',
        body: 'Cổ phiếu có thể được chuyển nhượng cho thế hệ tiếp theo một cách đơn giản và tối ưu về thuế.' },
    ],
    processTitle: 'Cách lợi nhuận của bạn được tạo ra',
    processSubtitle: 'Chuỗi giá trị bốn bước đã được kiểm chứng',
    steps: [
      { icon: Search,       title: 'Tìm kiếm',    body: 'Chúng tôi xác định những bất động sản Thụy Sĩ bị định giá thấp với tiềm năng rõ ràng.' },
      { icon: Hammer,       title: 'Tối ưu hóa',  body: 'Sửa chữa, hiện đại hóa và cho thuê chuyên nghiệp tăng giá trị và thu nhập bền vững.' },
      { icon: ArrowUpRight, title: 'Bán lại',     body: 'Đúng thời điểm, chúng tôi bán các tài sản với lợi nhuận hấp dẫn cho người mua tổ chức.' },
      { icon: Banknote,     title: 'Phân phối',   body: 'Lợi nhuận được trả lại dưới dạng cổ tức gộp trực tiếp vào tài khoản của bạn.' },
    ],
    minTitle: 'Tham gia chỉ từ',
    minValue: 'CHF 3,000',
    minBody: 'Trở thành cổ đông chính thức được đăng ký với khoản đầu tư tối thiểu hợp lý – ghi nhận trực tiếp vào sổ đăng ký cổ đông Thụy Sĩ chính thức.',
    cardTitle: 'Sắp ra mắt: Thẻ Löwenherz',
    cardBody: 'Cổ tức của bạn trực tiếp trên thẻ tín dụng Löwenherz cá nhân – linh hoạt trên toàn thế giới.',
    cta: 'Trở thành cổ đông',
  },
};

const Benefit = ({ icon: Icon, title, body, idx }) => (
  <div
    data-testid={`benefit-card-${idx}`}
    className="group bg-[#1a1a1a] border border-[#C4A55A]/20 hover:border-[#C4A55A]/55 rounded-2xl p-5 transition-all duration-300 hover:-translate-y-0.5"
  >
    <div className="w-11 h-11 rounded-xl bg-[#C4A55A]/15 border border-[#C4A55A]/40 flex items-center justify-center mb-3 group-hover:bg-[#C4A55A]/25 transition-colors">
      <Icon className="h-5 w-5 text-[#C4A55A]" />
    </div>
    <h3 className="text-white font-semibold mb-1.5">{title}</h3>
    <p className="text-sm text-gray-300 leading-relaxed">{body}</p>
  </div>
);

const Step = ({ icon: Icon, title, body, num, last }) => (
  <div className="relative bg-[#1a1a1a] border border-[#C4A55A]/15 rounded-xl p-4">
    <div className="flex items-center gap-3 mb-2">
      <div className="w-8 h-8 rounded-lg bg-[#C4A55A]/20 border border-[#C4A55A]/40 flex items-center justify-center">
        <Icon className="h-4 w-4 text-[#C4A55A]" />
      </div>
      <span className="text-xs text-[#C4A55A] uppercase tracking-wider font-bold">Schritt {num}</span>
    </div>
    <h4 className="text-white font-semibold mb-1.5">{title}</h4>
    <p className="text-sm text-gray-400 leading-relaxed">{body}</p>
    {!last && (
      <ChevronRight className="hidden md:block absolute -right-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#C4A55A]/40" />
    )}
  </div>
);

export const WarumLoewenherz = () => {
  const { language } = useLanguage();
  const t = T[language] || T.de;

  const scrollToInvestors = () => {
    const el = document.getElementById('investors');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="warum" className="py-20 bg-gradient-to-b from-black via-[#0a0a0a] to-black relative">
      {/* Decorative golden line */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-px bg-gradient-to-r from-transparent via-[#C4A55A] to-transparent opacity-50" />

      <div className="container mx-auto px-4">
        {/* Headline */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <div className="inline-flex items-center px-3 py-1 rounded-full bg-[#C4A55A]/15 border border-[#C4A55A]/30 text-[#C4A55A] text-xs uppercase tracking-wider font-semibold mb-4">
            {t.eyebrow}
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            {t.title}{' '}
            <span className="bg-gradient-to-r from-[#C4A55A] via-[#D4B56A] to-[#C4A55A] bg-clip-text text-transparent">
              {t.titleHighlight}
            </span>
          </h2>
          <p className="text-gray-300 text-lg leading-relaxed">{t.intro}</p>
        </div>

        {/* Benefits grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {t.benefits.map((b, i) => (
            <Benefit key={i} idx={i} icon={b.icon} title={b.title} body={b.body} />
          ))}
        </div>

        {/* Value chain */}
        <div className="text-center mb-8">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">{t.processTitle}</h3>
          <p className="text-gray-400">{t.processSubtitle}</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-16">
          {t.steps.map((s, i) => (
            <Step key={i} num={i + 1} icon={s.icon} title={s.title} body={s.body} last={i === t.steps.length - 1} />
          ))}
        </div>

        {/* Min investment + Card teaser */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-12">
          <div className="bg-gradient-to-br from-[#C4A55A]/15 via-[#1a1a1a] to-[#1a1a1a] border border-[#C4A55A]/30 rounded-2xl p-7">
            <div className="text-xs uppercase tracking-wider text-[#C4A55A] font-semibold mb-2">{t.minTitle}</div>
            <div className="text-4xl md:text-5xl font-bold text-white mb-3 tabular-nums">{t.minValue}</div>
            <p className="text-sm text-gray-300 leading-relaxed">{t.minBody}</p>
          </div>
          <div className="bg-gradient-to-br from-[#1a1a1a] via-[#15110a] to-[#1a1a1a] border border-[#C4A55A]/30 rounded-2xl p-7 relative overflow-hidden">
            <div className="absolute top-3 right-3 px-2 py-0.5 bg-amber-500/15 border border-amber-500/30 text-amber-300 text-[10px] uppercase tracking-wider rounded-full">
              {language === 'en' ? 'Coming soon' : language === 'ru' ? 'Скоро' : language === 'vi' ? 'Sắp ra mắt' : 'In Planung'}
            </div>
            <CreditCard className="h-9 w-9 text-[#C4A55A] mb-3" />
            <h3 className="text-xl font-bold text-white mb-2">{t.cardTitle}</h3>
            <p className="text-sm text-gray-300 leading-relaxed">{t.cardBody}</p>
          </div>
        </div>

        <div className="text-center">
          <button
            onClick={scrollToInvestors}
            data-testid="warum-cta"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-r from-[#C4A55A] to-[#B8974E] hover:from-[#D4B56A] hover:to-[#C4A55A] text-black font-semibold text-base shadow-lg shadow-[#C4A55A]/20 hover:shadow-[#C4A55A]/40 transition-all hover:scale-[1.02]"
          >
            {t.cta}
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>
    </section>
  );
};
